// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import type { CloudInfo, PoseGraph, PoseGraphEdgeType } from '../api/types';
import { useCanEdit } from '../app/CaseRoleContext';
import { explainLoadError } from '../app/errorCopy';
import { useJobs } from '../app/JobsContext';
import { useAsync } from '../app/useAsync';
import { Button } from '../components/Button';
import { Checkbox } from '../components/Checkbox';
import { FramePairInspector } from '../components/FramePairInspector';
import { PoseGraphEdgeInspector, type AddSeed } from '../components/PoseGraphEdgeInspector';
import { SegmentedControl } from '../components/SegmentedControl';
import { TextInput } from '../components/TextInput';
import { TextLink } from '../components/TextLink';
import { parseFrameId } from '../data/framePair';
import {
  EDGE_TYPES,
  EDGE_TYPE_LABEL,
  countAbove,
  edgeBox,
  edgeCounts,
  edgeParam,
  edgeSwatchCss,
  findEdge,
  formatResidual,
  graphSummary,
  latestOptimizeJob,
  optimizeDoneKey,
  parseEdgeParam,
  planeView,
  residualScale,
  strainedEdges,
  type EdgeRef,
} from '../posegraf/model';
import type { ViewPreset } from '../viewport/cameraViews';
import type { ProjectionPlane } from '../viewport/posegraphLayer';
import type { SelectedEdge } from '../viewport/PoseGraphScene';
import { Viewport, type ViewportHandle, type ViewportLayer } from '../viewport/Viewport';
import styles from './GraphViewPage.module.css';
import { redigerHref } from '../bygning/rediger';
import { BlocksPanel, BoxSelectOverlay, HistoryPanel, TransformPanel } from './posegraf/EditorPanels';
import { usePosegrafEditor } from './posegraf/usePosegrafEditor';

const RENDERABLE = new Set(['PointXYZRGB', 'PointXYZ']);
const PLANES: readonly { value: ProjectionPlane; label: string }[] = [
  { value: '3D', label: '3D' },
  { value: 'XY', label: 'Plan' },
  { value: 'XZ', label: 'Facade' },
  { value: 'YZ', label: 'Side' },
];
const NODE_COLOURS: readonly { value: 'default' | 'degree'; label: string }[] = [
  { value: 'default', label: 'Ens' },
  { value: 'degree', label: 'Efter antal kanter' },
];
const ALL_TYPES: Record<PoseGraphEdgeType, boolean> = { odometry: true, loop_closure: true, panorama: true };
/** In Blokke the bones carry the trajectory; only the loops are drawn over them. */
const LOOP_TYPES: Record<PoseGraphEdgeType, boolean> = { odometry: false, loop_closure: true, panorama: true };
type PageMode = 'kanter' | 'blokke';
const MODES: readonly { value: PageMode; label: string }[] = [
  { value: 'kanter', label: 'Kanter' },
  { value: 'blokke', label: 'Blokke' },
];

/**
 * Posegraf (frontend-r22.7): the tool for fixing the scan's pose graph —
 * Rediger › Scan & pose since frontend-u0o.1 (`/rediger/scan`; the old
 * `/graph-view` redirects here with its query). The shell lays it over the
 * Ground as a cover below its top bar and sub-tabs.
 *
 * The 3D view shows the stored graph over a thinned copy of the cloud
 * (expert canvas, near-black in both themes). Left panel: Visning
 * (projection, node colour, edge types, residual threshold) and the most
 * strained edges. Right panel: the edge inspector — afvis, tilføj, ICP,
 * genopret and Kør optimering. Bottom drawer: Billedpar, the frame-pair
 * compare (`?a=&b=`), whose pair can be turned into an edge.
 *
 * URL state: `?edge=from-to-type` (the selection), `?a=&b=` (the pair),
 * `?par=1` (drawer open) — every view is a shareable link, as before.
 *
 * Blokke (`?tilstand=blokke`, frontend-aev): the scan-repair editor over
 * ruxd PR #19 — import several RTAB-Map sessions (in Rediger › Import), split the trajectory into
 * blocks (suggested splits with their reasons), select bones / chains /
 * boxes, move them with the gizmo (live preview, blended joints from
 * `preview: true`), apply as a pose version, and undo / redo / restore /
 * compare in Historik. State: `posegraf/usePosegrafEditor.ts`.
 */
export function GraphViewPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const viewportRef = useRef<ViewportHandle>(null);

  // ---------------------------------------------------------------- data --
  const graphRead = useAsync<PoseGraph>((signal) => api.posegraph(signal), []);
  const cloudsRead = useAsync<CloudInfo[]>((signal) => api.clouds(signal), []);
  const graph = graphRead.data ?? null;
  const reloadGraph = graphRead.reload;
  const mode: PageMode = params.get('tilstand') === 'blokke' ? 'blokke' : 'kanter';
  const editing = mode === 'blokke';
  const editor = usePosegrafEditor({ active: editing, graph, reloadGraph });
  const [showLoops, setShowLoops] = useState(true);

  // A finished optimize run moved the poses and rewrote the residuals.
  const { jobs } = useJobs();
  const doneKey = optimizeDoneKey(latestOptimizeJob(jobs));
  const seenDone = useRef(doneKey);
  useEffect(() => {
    if (doneKey === seenDone.current) return;
    seenDone.current = doneKey;
    if (doneKey) reloadGraph();
  }, [doneKey, reloadGraph]);

  const primary = useMemo(() => {
    const renderable = (cloudsRead.data ?? []).filter((c) => RENDERABLE.has(c.type));
    return renderable.find((c) => c.name === 'cloud') ?? renderable[0] ?? null;
  }, [cloudsRead.data]);

  // ---------------------------------------------------------- view state --
  const [plane, setPlane] = useState<ProjectionPlane>('3D');
  const [nodeColor, setNodeColor] = useState<'default' | 'degree'>('default');
  const [types, setTypes] = useState<Record<PoseGraphEdgeType, boolean>>(ALL_TYPES);
  const [threshold, setThreshold] = useState(0);
  const [showCloud, setShowCloud] = useState(true);
  const [view, setView] = useState<{ preset: ViewPreset; nonce: number } | null>(null);
  const [frameNonce, setFrameNonce] = useState(0);
  const [addSeed, setAddSeed] = useState<AddSeed | null>(null);

  const changePlane = (p: ProjectionPlane) => {
    setPlane(p);
    const v = planeView(p);
    if (v) setView({ preset: v.preset, nonce: Date.now() });
  };

  const layers = useMemo<ViewportLayer[]>(
    () => (primary ? [{ cloud: primary.name, visible: showCloud, overviewPoints: 60_000, lodOnly: true }] : []),
    [primary, showCloud],
  );

  // ----------------------------------------------------------- selection --
  const ref = parseEdgeParam(params.get('edge'));
  const edge = findEdge(graph, ref);
  const selected = useMemo<SelectedEdge | null>(
    () => (edge ? { from: edge.from, to: edge.to, type: edge.type, residual: edge.residual, weight: edge.weight } : null),
    // A reload hands back new edge objects, so the scene (rebuilt by the
    // reload) is told the selection again.
    [edge],
  );

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const select = useCallback((r: EdgeRef | null) => patch({ edge: r ? edgeParam(r) : null }), [patch]);

  const focus = (r: EdgeRef) => {
    select(r);
    if (plane !== '3D') return;
    const box = edgeBox(graph, r);
    if (box) viewportRef.current?.frameWorldBox(box.min, box.max);
  };

  const frameA = parseFrameId(params.get('a'));
  const frameB = parseFrameId(params.get('b'));
  const pairOpen = params.get('par') === '1';
  const compare = (from: number, to: number) => patch({ a: String(from), b: String(to), par: '1' });

  // --------------------------------------------------------------- derived --
  const counts = edgeCounts(graph);
  const strained = strainedEdges(graph, types, 8);
  const scale = residualScale(graph);
  const above = countAbove(graph, threshold);
  const summary = graphSummary(graph, graphRead.error);
  const loadError = graphRead.error ? explainLoadError(graphRead.error, 'posegrafen') : null;
  const hasEdges = (graph?.edges.length ?? 0) > 0;

  return (
    <div className={styles.page}>
      <div className={styles.stage}>
        <div className={styles.canvas} role="region" aria-label="Posegrafen i 3D">
          <Viewport
            ref={viewportRef}
            layers={layers}
            colorMode="rgb"
            pointSize={1}
            pointSizeUnit="px"
            frameToken={frameNonce}
            projection={plane === '3D' ? 'perspective' : 'orthographic'}
            view={view}
            poseGraph={graph}
            poseGraphVisible={!editing || showLoops}
            poseGraphProjectionPlane={plane}
            poseGraphEdgeTypeVisible={editing ? LOOP_TYPES : types}
            poseGraphResidualThreshold={threshold}
            poseGraphNodeColorMode={nodeColor}
            poseGraphSelectedEdge={selected}
            onPickPoseGraphEdge={editing ? undefined : (hit) => select(hit)}
            onScene={editor.setHost}
            onCanvasClick={editing ? editor.onCanvasClick : undefined}
          />
          {editing && editor.boxMode && <BoxSelectOverlay onBox={editor.onBox} />}
          {graphRead.loading && !graph && (
            <p className={styles.status} role="status">
              Posegrafen indlæses…
            </p>
          )}
        </div>

        <div className={styles.overlay}>
          <aside className={`${styles.panel} ${styles.left}`} aria-label="Visning">
            <header className={styles.panelHead}>
              <h1 className={styles.title}>Posegraf</h1>
              {summary && <p className={styles.summary}>{summary}</p>}
              <SegmentedControl
                aria-label="Tilstand"
                items={MODES}
                value={mode}
                onChange={(m) => patch({ tilstand: m === 'blokke' ? 'blokke' : null, edge: null, par: null })}
              />
            </header>

            {loadError && (
              <div className={styles.error} role="alert">
                <strong>{loadError.heading}</strong>
                <span>{loadError.message}</span>
                {loadError.retryIsTheAnswer && (
                  <TextLink size="sm" onClick={reloadGraph}>
                    Prøv igen
                  </TextLink>
                )}
              </div>
            )}

            <section className={styles.group} aria-labelledby="pg-view">
              <h2 id="pg-view" className={styles.heading}>
                Visning
              </h2>
              <SegmentedControl aria-label="Projektion" items={PLANES} value={plane} onChange={changePlane} />
              <span className={styles.label}>Knudefarve</span>
              <SegmentedControl aria-label="Knudefarve" items={NODE_COLOURS} value={nodeColor} onChange={setNodeColor} />
              <div className={styles.checks}>
                <Checkbox
                  label="Punktsky som baggrund"
                  checked={showCloud}
                  disabled={!primary}
                  onChange={(e) => setShowCloud(e.target.checked)}
                />
              </div>
              <TextLink size="sm" onClick={() => setFrameNonce((n) => n + 1)}>
                Centrér visningen
              </TextLink>
            </section>

            {editing && (
              <>
                <Checkbox label="Vis løkkekanter" checked={showLoops} onChange={(e) => setShowLoops(e.target.checked)} />
                <BlocksPanel
                  editor={editor}
                  canEdit={canEdit}
                  onImport={() => navigate(redigerHref('import', { source: 'rtabmap' }))}
                  onFocus={(node) => editor.focusNode(node, (min, max) => viewportRef.current?.frameWorldBox(min, max))}
                />
              </>
            )}

            {!editing && hasEdges && (
              <section className={styles.group} aria-labelledby="pg-types">
                <h2 id="pg-types" className={styles.heading}>
                  Kanttyper
                </h2>
                <div className={styles.checks}>
                  {EDGE_TYPES.map((t) => (
                    <Checkbox
                      key={t}
                      muted={counts[t] === 0}
                      label={
                        <span className={styles.edgeLabel}>
                          <span className={styles.swatch} style={{ background: edgeSwatchCss(t) }} aria-hidden="true" />
                          {EDGE_TYPE_LABEL[t]}
                        </span>
                      }
                      hint={<span className="mono">{counts[t].toLocaleString('da-DK')}</span>}
                      checked={types[t]}
                      onChange={(e) => setTypes((prev) => ({ ...prev, [t]: e.target.checked }))}
                    />
                  ))}
                </div>

                <label className={styles.range}>
                  <span className={styles.rangeHead}>
                    <span className={styles.label}>Fremhæv residual over</span>
                    <span className="mono">{threshold === 0 ? 'fra' : formatResidual(threshold)}</span>
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={scale}
                    step={scale / 100}
                    value={Math.min(threshold, scale)}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                  />
                  {threshold > 0 && (
                    <small className={styles.hint}>
                      {above.toLocaleString('da-DK')} {above === 1 ? 'kant' : 'kanter'} fremhævet med gult
                    </small>
                  )}
                </label>
              </section>
            )}

            {!editing && strained.length > 0 && (
              <section className={styles.group} aria-labelledby="pg-strained">
                <h2 id="pg-strained" className={styles.heading}>
                  Mest belastede kanter
                </h2>
                <ol className={styles.strained}>
                  {strained.map((e) => {
                    const key = edgeParam(e);
                    const on = edge !== null && edgeParam(edge) === key;
                    return (
                      <li key={key}>
                        <button
                          type="button"
                          className={`${styles.strainedRow}${on ? ` ${styles.strainedOn}` : ''}`}
                          aria-pressed={on}
                          onClick={() => focus(e)}
                        >
                          <span className={styles.swatch} style={{ background: edgeSwatchCss(e.type) }} aria-hidden="true" />
                          <span className="mono">
                            {e.from} – {e.to}
                          </span>
                          <span className={`${styles.residual} mono`}>{formatResidual(e.residual)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}
          </aside>

          {editing ? (
            <aside className={`${styles.panel} ${styles.right} ${styles.editorRight}`} aria-label="Flyt og historik">
              <TransformPanel editor={editor} canEdit={canEdit} />
              {editor.notice && (
                <p className={`${styles.notice} ${styles[`notice_${editor.notice.tone}`] ?? ''}`} role="status">
                  {editor.notice.text}
                </p>
              )}
              <HistoryPanel editor={editor} canEdit={canEdit} />
            </aside>
          ) : (
            <aside className={`${styles.panel} ${styles.right}`} aria-label="Kant" aria-live="polite">
              <PoseGraphEdgeInspector
                graph={graph}
                edge={edge}
                onChanged={reloadGraph}
                onSelect={select}
                onCompare={compare}
                addSeed={addSeed}
              />
            </aside>
          )}

          {!editing && (
          <section className={`${styles.panel} ${styles.pair}${pairOpen ? ` ${styles.pairOpen}` : ''}`} aria-label="Billedpar">
            <div className={styles.pairHead}>
              <h2 className={styles.heading}>Billedpar</h2>
              <FramePicker key={`a-${frameA ?? ''}`} label="A" frameId={frameA} onSet={(v) => patch({ a: v })} />
              <Button
                variant="outline"
                size="sm"
                onClick={() => patch({ a: params.get('b'), b: params.get('a') })}
                aria-label="Byt billede A og B"
                title="Byt A og B"
              >
                ⇄
              </Button>
              <FramePicker key={`b-${frameB ?? ''}`} label="B" frameId={frameB} onSet={(v) => patch({ b: v })} />
              <div className={styles.pairActions}>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={frameA === null || frameB === null || frameA === frameB}
                    onClick={() => setAddSeed({ from: frameA, to: frameB, nonce: Date.now() })}
                    title="Åbn Tilføj kant med billede A og B"
                  >
                    Opret kant fra parret
                  </Button>
                )}
                <TextLink
                  size="sm"
                  aria-expanded={pairOpen}
                  onClick={() => patch({ par: pairOpen ? null : '1' })}
                >
                  {pairOpen ? 'Skjul sammenligning' : 'Sammenlign'}
                </TextLink>
              </div>
            </div>
            {pairOpen && (
              <div className={styles.pairBody}>
                <FramePairInspector frameA={frameA} frameB={frameB} />
              </div>
            )}
          </section>
          )}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------- //

/**
 * A compact frame picker: a number field plus a live thumbnail. The draft is
 * set at mount; the parent remounts it (via `key`) when the URL changes from
 * outside (Byt, a Sammenlign from the inspector).
 */
function FramePicker({ label, frameId, onSet }: { label: string; frameId: number | null; onSet: (value: string | null) => void }) {
  const [draft, setDraft] = useState(frameId !== null ? String(frameId) : '');
  const [thumbFailed, setThumbFailed] = useState(false);
  const commit = (raw: string) => {
    const id = parseFrameId(raw.trim());
    onSet(id !== null ? String(id) : null);
  };
  const preview = parseFrameId(draft);
  return (
    <label className={styles.picker}>
      <span className={styles.pickerLabel}>{label}</span>
      <TextInput
        variant="outline"
        size="sm"
        mono
        type="text"
        inputMode="numeric"
        className={styles.pickerInput}
        value={draft}
        placeholder="billede"
        onChange={(e) => {
          setDraft(e.target.value);
          setThumbFailed(false);
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
        }}
      />
      {preview !== null && !thumbFailed && (
        <img
          className={styles.pickerThumb}
          src={api.frameImageUrl(preview, 'color', { maxSize: 80 })}
          alt=""
          decoding="async"
          onError={() => setThumbFailed(true)}
        />
      )}
    </label>
  );
}
